const SHARE_SITE_ORIGIN = "https://www.albavox.com";

export enum ShareContentType {
  Post = 1,
  Trip = 2,
  Story = 3,
  Meetup = 4,
  Event = 5,
  Template = 6,
  Place = 7,
  Tour = 8,
  TravelPackage = 9,
  Activity = 10,
  ItineraryTemplate = 11,
}

export type LegacyShareType = "profile" | "adventure" | "beach" | "culture" | "nature" | "photospot";

export type DecodedShareSlug =
  | { kind: "content"; contentType: ShareContentType; contentId: number }
  | { kind: "legacy"; legacyType: LegacyShareType | "event"; legacyKey: string };

export const SHARE_PATH_PREFIX = {
  post: "p",
  trip: "t",
  story: "s",
  meetup: "m",
  event: "e",
  place: "l",
  tour: "v",
  offer: "o",
  profile: "u",
  adventure: "a",
  beach: "b",
  culture: "c",
  nature: "n",
  photospot: "f",
} as const;

const LEGACY_KEYED_SLUG_PREFIXES: Record<string, LegacyShareType> = {
  U: "profile",
  A: "adventure",
  B: "beach",
  C: "culture",
  N: "nature",
  F: "photospot",
};

const NUMERIC_PREFIX_TO_CONTENT: Record<string, ShareContentType> = {
  [SHARE_PATH_PREFIX.post]: ShareContentType.Post,
  [SHARE_PATH_PREFIX.trip]: ShareContentType.Trip,
  [SHARE_PATH_PREFIX.story]: ShareContentType.Story,
  [SHARE_PATH_PREFIX.meetup]: ShareContentType.Meetup,
  [SHARE_PATH_PREFIX.event]: ShareContentType.Event,
  [SHARE_PATH_PREFIX.place]: ShareContentType.Place,
  [SHARE_PATH_PREFIX.tour]: ShareContentType.Tour,
  [SHARE_PATH_PREFIX.offer]: ShareContentType.TravelPackage,
};

const STRING_PREFIX_TO_LEGACY: Record<string, LegacyShareType> = {
  [SHARE_PATH_PREFIX.profile]: "profile",
  [SHARE_PATH_PREFIX.adventure]: "adventure",
  [SHARE_PATH_PREFIX.beach]: "beach",
  [SHARE_PATH_PREFIX.culture]: "culture",
  [SHARE_PATH_PREFIX.nature]: "nature",
  [SHARE_PATH_PREFIX.photospot]: "photospot",
};

function base64UrlDecodeToBytes(input: string): Uint8Array | null {
  if (!input) return null;
  try {
    const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

function decodeNumericSlug(slug: string): number | null {
  const bytes = base64UrlDecodeToBytes(slug);
  if (!bytes || bytes.length !== 4) return null;
  const id = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, true);
  return id > 0 ? id : null;
}

function decodeStringSlug(slug: string): string | null {
  const bytes = base64UrlDecodeToBytes(slug);
  if (!bytes || bytes.length === 0) return null;
  const key = new TextDecoder().decode(bytes);
  return key ? key : null;
}

function decodeLegacyPackedSlug(slug: string): DecodedShareSlug | null {
  const normalized = slug.trim();
  if (!normalized) return null;

  const keyedPrefix = normalized[0];
  const legacyType = LEGACY_KEYED_SLUG_PREFIXES[keyedPrefix];
  if (legacyType && normalized.length > 1) {
    const key = decodeStringSlug(normalized.slice(1));
    if (!key) return null;
    return { kind: "legacy", legacyType, legacyKey: key };
  }

  const bytes = base64UrlDecodeToBytes(normalized);
  if (!bytes || bytes.length !== 8) return null;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const packed = view.getBigUint64(0, true);
  const contentType = Number(packed >> 32n) as ShareContentType;
  const contentId = Number(packed & 0xffffffffn);
  if (!contentId || !Object.values(ShareContentType).includes(contentType)) {
    return null;
  }

  return { kind: "content", contentType, contentId };
}

export function decodeShareLink(
  pathPrefix: string | undefined,
  slug: string,
): DecodedShareSlug | null {
  const normalizedSlug = slug?.trim();
  if (!normalizedSlug) return null;

  const prefix = pathPrefix?.trim().toLowerCase();

  if (prefix && NUMERIC_PREFIX_TO_CONTENT[prefix]) {
    const contentId = decodeNumericSlug(normalizedSlug);
    if (!contentId) {
      if (prefix === SHARE_PATH_PREFIX.post) {
        return decodeLegacyPackedSlug(normalizedSlug);
      }
      return null;
    }
    return {
      kind: "content",
      contentType: NUMERIC_PREFIX_TO_CONTENT[prefix],
      contentId,
    };
  }

  if (prefix && STRING_PREFIX_TO_LEGACY[prefix]) {
    const legacyKey = decodeStringSlug(normalizedSlug);
    if (!legacyKey) return null;
    return { kind: "legacy", legacyType: STRING_PREFIX_TO_LEGACY[prefix], legacyKey };
  }

  return decodeLegacyPackedSlug(normalizedSlug);
}

export function decodeShareSlug(slug: string): DecodedShareSlug | null {
  return decodeShareLink(SHARE_PATH_PREFIX.post, slug) ?? decodeLegacyPackedSlug(slug);
}

export function resolveShareLink(pathPrefix: string, slug: string): boolean {
  return decodeShareLink(pathPrefix, slug) !== null;
}

/** @deprecated Use resolveShareLink(pathPrefix, slug) */
export function resolveShareSlugToPath(slug: string): string | null {
  return decodeShareSlug(slug) ? `/p/${slug}` : null;
}

export function getShareSiteOrigin(): string {
  return SHARE_SITE_ORIGIN;
}

export const SHARE_LINK_PREFIXES = Object.values(SHARE_PATH_PREFIX);
